// src/components/Markdown.jsx — terminal-styled renderer with markdown-aware <callout>
import ReactMarkdown                    from 'react-markdown';
import remarkGfm                        from 'remark-gfm';
import remarkBreaks                     from 'remark-breaks';
import rehypeRaw                        from 'rehype-raw';
import rehypeSanitize, { defaultSchema }from 'rehype-sanitize';
import { Prism as SyntaxHighlighter }   from 'react-syntax-highlighter';
import { vscDarkPlus as codeTheme }     from 'react-syntax-highlighter/dist/esm/styles/prism';

import LivePythonSandbox from './LivePythonSandbox';

/* ── palette ---------------------------------------------------------------- */
const ACCENT_TEXT = 'text-green-400';
const PANEL_BG    = 'bg-[#161b22]';
const BORDER_CLR  = 'border-[#30363d]';
const MUTED_TEXT  = 'text-[#8b949e]';

/* ── call-out panel --------------------------------------------------------- */
function Callout({ type = 'info', children }) {
  const colour =
    { info: 'blue', tip: 'green', note: 'yellow', warn: 'orange', danger: 'red' }[type] ?? 'blue';

  const colours = {
    blue:   'bg-blue-500/20  text-blue-400   border-blue-400',
    green:  'bg-green-500/20 text-green-400  border-green-500',
    yellow: 'bg-yellow-400/20 text-yellow-300 border-yellow-300',
    orange: 'bg-orange-500/20 text-orange-400 border-orange-400',
    red:    'bg-red-500/20   text-red-400    border-red-400',
  };

  return (
    <aside className={`my-8 p-4 ${PANEL_BG} border-l-4 rounded-lg ${colours[colour]}`}>
      {children}
    </aside>
  );
}

/* ── extend rehype-sanitize so <callout> isn’t stripped ---------------------- */
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...defaultSchema.tagNames, 'callout'],
  attributes: { ...defaultSchema.attributes, callout: ['type'] },
};

export default function Markdown({ children }) {
  return (
    <div className="prose prose-invert max-w-none
                    prose-headings:font-semibold prose-headings:scroll-mt-24
                    prose-img:rounded-lg prose-img:border prose-img:border-[#30363d] prose-img:shadow-lg
                    prose-code:font-mono
                    prose-pre:bg-[#1e1e1e] prose-pre:rounded-xl prose-pre:p-4 prose-pre:border prose-pre:border-[#30363d]
                    lg:prose-lg">

      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[
          rehypeRaw,
          [rehypeSanitize, sanitizeSchema],
        ]}
        components={{
          /* headings -------------------------------------------------------- */
          h1: p => <h1 className="text-4xl font-bold mb-4 mt-12 scroll-mt-24" {...p} />,
          h2: p => <h2 className={`text-3xl font-bold mb-3 mt-10 ${ACCENT_TEXT} scroll-mt-24`} {...p} />,
          h3: p => <h3 className="text-2xl font-semibold mb-3 mt-8 scroll-mt-24" {...p} />,

          /* text blocks ----------------------------------------------------- */
          p : p => <p  className="leading-8 mb-5" {...p} />,
          ul: p => <ul className="list-disc   list-inside space-y-1 mb-5" {...p} />,
          ol: p => <ol className="list-decimal list-inside space-y-1 mb-5" {...p} />,
          blockquote: p => (
            <blockquote
              className={`${PANEL_BG} ${BORDER_CLR} border-l-4 pl-4 py-3 my-6 italic ${MUTED_TEXT}`}
              {...p}
            />
          ),

          /* images ---------------------------------------------------------- */
          img: ({ src, alt }) => (
            <figure className="my-8 flex flex-col items-center">
              <img src={src} alt={alt} className={`max-w-full rounded-lg border ${BORDER_CLR}`} />
              {alt && <figcaption className={`mt-2 text-sm ${MUTED_TEXT}`}>{alt}</figcaption>}
            </figure>
          ),

          /* call-out tag with inner markdown -------------------------------- */
          callout: ({ node, children }) => {
            const inner = Array.isArray(children) ? children.join('') : children;
            return (
              <Callout type={node.properties?.type}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  rehypePlugins={[]}
                  components={{ p: p => <p className="leading-7 mb-2" {...p} /> }}
                >
                  {inner}
                </ReactMarkdown>
              </Callout>
            );
          },

          /* code blocks & inline code --------------------------------------- */
          code({ inline, className = '', children, ...props }) {
            const lang = /language-(\w+)/.exec(className)?.[1];
            const raw  = String(children).replace(/\n$/, '');

            if (inline) {
              return (
                <code className="bg-[#21262d] px-1.5 py-0.5 rounded text-[#d2a8ff] text-[0.925rem]" {...props}>
                  {children}
                </code>
              );
            }

            if (lang === 'python') return <LivePythonSandbox initialCode={raw} />;

            if (!lang && !raw.includes('\n')) {
              return (
                <code className="bg-[#21262d] px-2 py-1 rounded text-[#d2a8ff] text-sm" {...props}>
                  {raw.trim()}
                </code>
              );
            }

            return (
              <SyntaxHighlighter
                style={codeTheme}
                language={lang ?? 'text'}
                PreTag="div"
                customStyle={{
                  background: '#1e1e1e',
                  borderRadius: '0.75rem',
                  border: '1px solid #30363d',
                  padding: '1rem 1.25rem',
                  margin: '1.5rem 0',
                  fontSize: '0.9rem',
                }}
                {...props}
              >
                {raw}
              </SyntaxHighlighter>
            );
          },
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
